'use strict';
import _ from 'lodash';
import Order from './order.js';
import {OrderType} from './orderType.js';
import {storage, events, RunConfigService} from "@nuskin/ns-util";
import {UserService} from "@nuskin/ns-account";
import {AdrService, CartService, CheckoutModeService} from "../shop";
import {getCachedConfigField} from '@nuskin/configuration-sdk';

const ORDER_STORAGE_KEY = storage.metadata.ORDER_STORAGE_KEY,
    ORDER_PREFS_STORAGE_KEY = storage.metadata.ORDER_PREFS_STORAGE_KEY,
    ORDER_EXTERNALID_KEY = storage.metadata.ORDER_EXTERNALID_KEY;

// Orders array.  It is possible for the user to have 2 orders so orders is an array.  1 being an order and the other an adr.
let orders;

// ---------------------------------------------
//
//         Private Methods
//
// ---------------------------------------------
/**
 * Initializes the orders array if it isn't already initialized.  It also subscribes to the login and logout events
 */
const _init = () => {
    if (!orders) {
        let orderJSON = storage.getItem({...ORDER_STORAGE_KEY, key: `${ORDER_STORAGE_KEY.key}-${RunConfigService.getRunConfig().country}`});
        if (orderJSON instanceof Array) {
            orders = orderJSON.map(order => new Order(order));
        } else {
            orders = [];
        }
    }
    events.subscribe(events.authentication.LOGIN, _clearOrderStorage);
    events.subscribe(events.authentication.LOGOUT, _clearOrderStorage);
};

/**
*   Creates an order and using the order preferences if available.
 *  @returns {Order}
 */
const _initOrder = () => {
    let orderPreferences = getOrderPreferences();
    let distributorName = "";
    let user = UserService.getUser();
    let buyerId = user !== null ? user.id : '';
    if (!CheckoutModeService.isGuestCheckout() && user.isDistributor()) {
        distributorName = user.getDisplayName();
    }

    let result = new Order({
        adr: false,
        countryCode: RunConfigService.getRunConfig().country,
        locale: `${RunConfigService.getRunConfig().language}_${RunConfigService.getRunConfig().country}`,
        soldToMemberId: buyerId,
        distributorName: distributorName,
        custom: [],
        cartTimestamp: CartService.getCartProperty('timestamp'),
        sapItems: CartService.getItemData({sapItems: true}),
        sapItemCnt: CartService.getCartInfo({sapItems: true}).qty,
        orderTotals: {},
        currencyCode: RunConfigService.getRunConfig().currencyCode
    });

    //Todo move this logic to a configuration.  Don't trigger off japan for this
    if ((result.countryCode === 'JP') && CartService.getItemData().some(item => item.isBusinessPortfolio)) {
        result.simulateWithoutShipping = true;
    }

    if (orderPreferences) {
        // If the user is the same user and hasn't changed the country use preferences
        if (orderPreferences.countryCode && orderPreferences.countryCode === result.countryCode && orderPreferences.soldToMemberId === buyerId) {
            result.selectedShippingMethod = orderPreferences.selectedShippingMethod;
            result.selectedAddress = orderPreferences.selectedAddress;
        }
    }
    if (!result.selectedShippingMethod) {
        if (getCachedConfigField('useShipMethodsApi')) {
            const csDefaultCode = getCachedConfigField('defaultShipMethod');
            if (csDefaultCode) {
                result.selectedShippingMethod = {Code: csDefaultCode, PickUpLocation: ''};
            }
        }
    }

    return new Order(result);
};

/**
 * Clear all order storage for user
 */
const _clearOrderStorage = () => {
    clearOrders();
    clearOrderPreferences();
    storage.removeItem(ORDER_EXTERNALID_KEY);
};

/**
 * Filters payment information out of the order object
 * @param {Object} order
 * @returns {Object} order
 */
const _filterCreditCard = (order) => {
    let o = Object.assign({}, order);
    o.paymentSecurityNumber = null;
    // If the selected payment is a credit card then we shouldn't store the selected payment in local storage
    if (o.selectedPayment && o.selectedPayment.paymentNumber) {
        if (o.selectedPayment && o.selectedPayment.bePaymentTypeId) {
            o.selectedPayment = {
                bePaymentTypeId: o.selectedPayment.bePaymentTypeId,
                paymentTypeId: o.externalPayment ? o.selectedPayment.paymentTypeId : null,
                paymentNumber: o.externalPayment ? o.selectedPayment.paymentNumber : null,
                expMonth: o.externalPayment ? o.selectedPayment.expMonth : null,
                expYear: o.externalPayment ? o.selectedPayment.expYear : null
            };
        }
    }
    return o;
};

/**
 * Gets the order preferences out of the order object
 * preferences are used to build orders.
 * @param order
 * @returns {any}
 * @private
 */
const _filterOrderPreferences = (order) => {
    return {
        selectedShippingMethod: order.selectedShippingMethod,
        selectedAddress: order.selectedAddress
    };
};

/**
 * sets the adr override number and reason on the order object
 * @param order
 * @private
 * @returns {Object} order
 */
const _handleAdrOverride = (order) => {
    if (AdrService.isAdrOverride()) {
        order.adrOverrideNumber = AdrService.getAdrOverrideId();
        order.adrOverrideReason = AdrService.getAdrOrderReason();
        order.orderType = OrderType.ADR_OVERRIDE;
    }
    return order;
};

// ---------------------------------------------
//
//        Public Methods
//
// ---------------------------------------------


/**
 * Setup the initial order(s) using the cart.  2 orders will be created if it is a ship future adr and the cart contains order items.
 * @param adrSignUpRequirementsNotMet
 * @param isOnlyOrder
 * @param isShipImmediateAdr
 * @returns {*}
 */
const setupOrders = (adrSignUpRequirementsNotMet, isShipImmediateAdr) => {     // Todo remove adrSignupRequirementsNotMet string parameter
    //Check to see if we need to create multiple orders
    if (!CartService.hasItems({cartAdrItems: true})) {
        let order = _initOrder();
        order.singleFlow = true;
        _handleAdrOverride(order);
        // If its not an override set the order type to single order
        order.orderType = order.orderType || OrderType.SINGLE_ORDER;
        addOrder(order);
    } else if (isShipImmediateAdr) {
        let order = _initOrder();
        order.orderType = OrderType.SHIP_IMMEDIATE;
        order.singleFlow = true;
        order.adr = true;
        addOrder(order);
    } else {
        if (CartService.hasItems({cartOrderItems: true})) {
            // We need to create an adr and a order. First determine where to place business portforlio
            if (CartService.isBusinessPortfolioAdr()) {
                // Adr first
                let adrOrder = _initOrder();
                adrOrder.orderType = OrderType.ADR_CREATE;
                adrOrder.adr = true;
                if (CartService.getCartProperty('inSignup') &&
                    RunConfigService.getRunConfig().country !== 'US' &&
                    RunConfigService.getRunConfig().country !== 'CA') {

                    adrOrder.infoMsg = adrSignUpRequirementsNotMet;
                }
                addOrder(adrOrder);
                let order = _initOrder();
                order.orderType = OrderType.SINGLE_ORDER;
                addOrder(order);
            } else {
                // Order first
                let order = _initOrder();
                order.orderType = OrderType.SINGLE_ORDER;
                addOrder(order);
                let adrOrder = _initOrder();
                adrOrder.adr = true;
                adrOrder.orderType = OrderType.ADR_CREATE;
                addOrder(adrOrder);
            }
        } else {
            // All we have is a ship future adr with no order items
            let adrOrder = _initOrder();
            adrOrder.singleFlow = true;
            adrOrder.adr = true;
            adrOrder.orderType = OrderType.ADR_CREATE;
            if (CartService.getCartProperty('inSignup') &&
                RunConfigService.getRunConfig().country !== 'US' &&
                RunConfigService.getRunConfig.country !== 'CA') {

                adrOrder.infoMsg = adrSignUpRequirementsNotMet;
            }
            addOrder(adrOrder);
        }
    }
    return orders;
}

/**
 * Writes the order json to local storage.  This will also update order preferences in localstorage
 * @memberOf OrderManager
 * @example OrderManager.saveOrders()
 */
const saveOrders = () => {
    let ordersJSON = getOrdersJSON().map(order => _filterCreditCard(order));
    //Filter out credit card data before saving to local storage
    storage.setItem({...ORDER_STORAGE_KEY, key: `${ORDER_STORAGE_KEY.key}-${RunConfigService.getRunConfig().country}`}, ordersJSON);
    if (ordersJSON.length > 0) {
        storage.setItem(ORDER_PREFS_STORAGE_KEY, _filterOrderPreferences(ordersJSON[0]));
    }
    events.publish(events.shop.ORDER_UPDATED, [orders]);
};

/**
 * @memberOf OrderManager
 * @example OrderManager.setOrders(orders)
 * @param newOrders
 * @returns [Order]
 */
const setOrders = (newOrders) => {
    if (newOrders && newOrders instanceof Array) {
        orders = newOrders.map(order => {
            if (order instanceof Order) {
                //update selected shipping method
                order.syncSelectedShippingMethod();
            } else {
                order = new Order(order);
            }
            return order;
        });
    } else {
        orders = [];
    }

    saveOrders();
    return orders;
}

/**
*   Get order preferences from localstorage
 *   @memberOf OrderManager
 *   @example OrderManager.getOrderPreferences
 *   @returns {Order}
 */
const getOrderPreferences = () => new Order(storage.getItem(ORDER_PREFS_STORAGE_KEY));

/**
 * Clear all orders out of storage.  Calls setOrders with no parameters which clears orders.
 * @memberOf OrderManager
 * @example OrderManager.clearOrders();
 */
const clearOrders = () => setOrders();

/**
 * Clear order preferences from localstorage
 * @memberOf OrderManager
 * @example OrderManager.clearOrderPreferences();
 */
const clearOrderPreferences = () => storage.removeItem(ORDER_PREFS_STORAGE_KEY);

/**
 * Returns array of orders in json
 * @memberOf OrderManager
 * @example OrderManager.getOrdersJSON
 * @returns [{Object}] orderJSON
 */
const getOrdersJSON = () => orders.map(order => order.toJSON());

/**
 * Returns orders
 * @returns [{Order}]
 */
const getOrders = () => orders;

/**
 * Add order to orders array
 * @memberOf OrderManager
 * @example OrderManager.addOrder(order)
 * @param {Object} order
 * @returns [{Order}]
 */
const addOrder = (order) => {
    (order instanceof Order) ? orders.push(order) : orders.push(new Order(order));
    saveOrders();
    return orders;
};

/**
 * Removes the order passed in from the array of orders
 * @param o
 * @returns [{order}] orders
 */
const removeOrder = (o) => {
    orders = orders.filter(order => !_.isEqual(order.toJSON(),o.toJSON()));
    saveOrders();
    return orders;
};

/**
 * Get next uncompleted order
 * @memberOf OrderManager
 * @example OrderManager.getOrder()
 * @returns {Order}
 */
const getOrder = () => orders.find(order => !order.completed);

/**
 * Get number of orders
 * @memberOf OrderManager
 * @example OrderManager.getNumOrders()
 * @returns {number} orderLength
 */
const getNumOrders = () => orders.length;

/**
 * Get the last completed order
 * @memberOf OrderManager
 * @example OrderManager.getCompletedOrder()
 * @returns {Order}
 */
const getCompletedOrder = () => orders.find(order => order.completed);

/**
 * Remove all completed orders from orders array
 * @memberOf OrderManager
 * @example OrderManager.removeCompletedOrders()
 * @returns {[Order]}
 */
const removeCompletedOrders = () => {
    orders = orders.filter(order => !order.completed);
    saveOrders();
    return orders;
};

/**
 * Set the externalId in localstorage.  This is called when create order is called
 * @memberOf OrderManager
 * @example OrderManager.setLastOrderId(externalId)
 * @param {String} externalId
 */
const setLastOrderId = (externalId) => {
    if (externalId) storage.setItem(ORDER_EXTERNALID_KEY, externalId);
};

/**
 * Get the externalId that was saved in localstorage
 * @memberOf OrderManager
 * @example OrderManager.getLastOrderId()
 */
const getLastOrderId = () => storage.getItem(ORDER_EXTERNALID_KEY);


// --------------------------------
//  Initialization
// --------------------------------
_init();

export default {
    addOrder,
    clearOrderPreferences,
    clearOrders,
    getCompletedOrder,
    getLastOrderId,
    getNumOrders,
    getOrder,
    getOrderPreferences,
    getOrders,
    getOrdersJSON,
    removeCompletedOrders,
    removeOrder,
    saveOrders,
    setLastOrderId,
    setOrders,
    setupOrders
};
